import { useEffect } from 'react';
import { api } from '../services/api';

/**
 * BrandTheme — injects the dealer's brand colours as CSS custom properties.
 *
 * Reads Dealer.brand_colors { primary, secondary } via GET /api/admin/dealer
 * (any authenticated role can read; only admin can write) and sets:
 *
 *   --brand-primary       → accents: buttons, links, active nav, badges
 *   --brand-primary-rgb   → "R,G,B" triplet for rgba() tints (e.g. message
 *                           bubble backgrounds) that need the colour at low
 *                           alpha: rgba(var(--brand-primary-rgb), 0.10)
 *   --brand-secondary     → secondary accents (neutral interactive chips)
 *
 * Components consume these via var(--brand-primary, #3b82f6). The fallback
 * preserves the original hardcoded colour for any element not yet swept or
 * when brand colours are unset.
 *
 * Mounted once near the root of the authenticated app in App.jsx.
 */

/** "#rrggbb" (or "#rgb") -> "r,g,b". Returns null on unparseable input. */
function hexToRgbTriplet(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `${r},${g},${b}`;
}

export default function BrandTheme() {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const dealer = await api.getDealerProfile();
        if (cancelled || !dealer) return;
        const colors = dealer.brand_colors || {};
        const root = document.documentElement;
        if (colors.primary) {
          root.style.setProperty('--brand-primary', colors.primary);
          const rgb = hexToRgbTriplet(colors.primary);
          if (rgb) root.style.setProperty('--brand-primary-rgb', rgb);
        }
        if (colors.secondary) root.style.setProperty('--brand-secondary', colors.secondary);
      } catch (e) {
        // Non-fatal: brand theming is cosmetic. Fall back to defaults.
        // eslint-disable-next-line no-console
        console.warn('BrandTheme: could not load dealer brand colours', e);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return null; // side-effect only component
}
